import React from 'react';

function ProductModal({ open, product, onClose }) {
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [zoomOrigin, setZoomOrigin] = React.useState({ x: 50, y: 50 });
  const [isWhatsAppFormOpen, setIsWhatsAppFormOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    nombre: '',
    producto: '',
    medioRetiro: '',
    medioPago: ''
  });
  const imageRef = React.useRef(null);
  const imageContainerRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      const prevOverflow = document.body.style.overflow;
      const prevPadding = document.body.style.paddingRight;
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.paddingRight = prevPadding;
      };
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setIsZoomed(false);
      setIsWhatsAppFormOpen(false);
      setFormData({
        nombre: '',
        producto: '',
        medioRetiro: '',
        medioPago: ''
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (isWhatsAppFormOpen && product) {
      setFormData(prev => ({
        ...prev,
        producto: product.name || ''
      }));
    }
  }, [isWhatsAppFormOpen, product]);

  if (!open || !product) return null;

  const { src, name = 'Producto', price = '$0', description, category } = product;

  const handleBackdrop = (e) => {
    if (e.target.classList.contains('tm-modal-backdrop')) {
      onClose();
    }
  };

  // Función para obtener el nombre de la sección desde la categoría
  const getSectionName = (cat) => {
    if (!cat) return '';
    if (cat.startsWith('cocina-')) return 'Cocina';
    if (cat.startsWith('mesadas-madera') || cat === 'mesadas-madera') return 'Mesadas de madera';
    if (cat.startsWith('mesadas-acero') || cat === 'mesadas-acero') return 'Mesadas de acero';
    if (cat.startsWith('mesadas-')) return 'Mesadas de marmolina';
    if (cat.startsWith('piletas-')) return 'Piletas';
    if (cat === 'muebles' || cat.startsWith('muebles-')) return 'Muebles';
    if (cat === 'combos' || cat.startsWith('combos-')) return 'Combos';
    return '';
  };

  const sectionName = getSectionName(category);
  // Formatear el número de teléfono con código de país (Argentina: 54)
  // Número local: 1176085834 -> Formato internacional: 5491176085834
  const phoneNumber = '5491176085834';

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    setIsWhatsAppFormOpen(true);
  };

  const handleWhatsAppFormClose = () => {
    setIsWhatsAppFormOpen(false);
    setFormData({
      nombre: '',
      producto: name,
      medioRetiro: '',
      medioPago: ''
    });
  };

  const handleWhatsAppFormBackdrop = (e) => {
    if (e.target.classList.contains('tm-wa-form-modal-backdrop')) {
      handleWhatsAppFormClose();
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWhatsAppFormSubmit = (e) => {
    e.preventDefault();
    
    const message = `Hola! Mi nombre es: ${formData.nombre}

Te hablo por el mueble: ${formData.producto}

Medio de retiro: ${formData.medioRetiro}

El medio de pago que voy a utilizar será: ${formData.medioPago}`;
    
    // Detectar si está en modo responsive (móvil) al momento del click
    const isMobile = window.innerWidth <= 650;
    const encodedMessage = encodeURIComponent(message);
    
    const whatsappHref = isMobile
      ? `https://wa.me/${phoneNumber}?text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    
    window.open(whatsappHref, '_blank', 'noopener,noreferrer');
    
    // Cerrar el formulario después de abrir WhatsApp
    handleWhatsAppFormClose();
  };

  const handleImageClick = (e) => {
    if (!imageRef.current || !imageContainerRef.current) return;
    
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomOrigin({ x, y });
    setIsZoomed(!isZoomed);
  };

  const handleImageMouseMove = (e) => {
    if (!isZoomed || !imageContainerRef.current) return;
    
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomOrigin({ x, y });
  };

  return (
    <>
      <div className="tm-modal-backdrop" onClick={handleBackdrop}>
        <div className="tm-modal" role="dialog" aria-modal="true">
          <button className="tm-modal-close" aria-label="Cerrar" onClick={onClose}>×</button>
          <div className="tm-modal-body tm-modal-vertical">
            <h3 className="tm-modal-title">{name}</h3>
            <div 
              className={`tm-modal-image ${isZoomed ? 'tm-modal-image-zoomed' : ''}`}
              ref={imageContainerRef}
              onClick={handleImageClick}
              onMouseMove={handleImageMouseMove}
              style={isZoomed ? {
                '--zoom-origin-x': `${zoomOrigin.x}%`,
                '--zoom-origin-y': `${zoomOrigin.y}%`
              } : {}}
            >
              <img src={src} alt={name} ref={imageRef} />
            </div>
            <div className="tm-modal-info tm-modal-info-bottom">
              <div className="tm-modal-price">{price}</div>
              {description && <p className="tm-modal-desc">{description}</p>}
              <a className="tm-wa-btn" href="#" onClick={handleWhatsAppClick}>
                Consultar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {isWhatsAppFormOpen && (
        <div className="tm-wa-form-modal-backdrop" onClick={handleWhatsAppFormBackdrop}>
          <div className="tm-wa-form-modal" role="dialog" aria-modal="true">
            <button className="tm-wa-form-modal-close" aria-label="Cerrar" onClick={handleWhatsAppFormClose}>×</button>
            <form className="tm-wa-form-modal-form" onSubmit={handleWhatsAppFormSubmit}>
              <div className="tm-wa-form-modal-header">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.372a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <h3 className="tm-wa-form-modal-title">Consulta por WhatsApp</h3>
              </div>
              
              <div className="tm-wa-form-modal-field">
                <label className="tm-wa-form-modal-label">Hola! Mi nombre es:</label>
                <input
                  type="text"
                  name="nombre"
                  className="tm-wa-form-modal-input"
                  value={formData.nombre}
                  onChange={handleFormChange}
                  placeholder="Tu nombre completo"
                  required
                />
              </div>

              <div className="tm-wa-form-modal-field">
                <label className="tm-wa-form-modal-label">Te hablo por el mueble:</label>
                <input
                  type="text"
                  name="producto"
                  className="tm-wa-form-modal-input"
                  value={formData.producto}
                  readOnly
                  required
                />
              </div>

              <div className="tm-wa-form-modal-field">
                <label className="tm-wa-form-modal-label">Medio de retiro:</label>
                <select
                  name="medioRetiro"
                  className="tm-wa-form-modal-select"
                  value={formData.medioRetiro}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Punto de entrega">Punto de entrega</option>
                  <option value="Retiro en local">Retiro en local</option>
                  <option value="Envío a domicilio">Envío a domicilio</option>
                </select>
              </div>

              <div className="tm-wa-form-modal-field">
                <label className="tm-wa-form-modal-label">El medio de pago que voy a utilizar será:</label>
                <select
                  name="medioPago"
                  className="tm-wa-form-modal-select"
                  value={formData.medioPago}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Tarjeta de crédito">Tarjeta de crédito</option>
                  <option value="Tarjeta de débito">Tarjeta de débito</option>
                  <option value="Mercado Pago">Mercado Pago</option>
                </select>
              </div>

              <button type="submit" className="tm-wa-form-modal-submit">
                Enviar consulta
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductModal;


